import { atom, useAtom, useAtomValue } from 'jotai';
import { concurrencyLimiter } from '@utils/queue/concurrencyUtils';

/**
 * Default concurrency limit for queries
 */
export const DEFAULT_CONCURRENT_QUERY_LIMIT = 3;

/**
 * Create a query manager with concurrency control
 */
export const createQueryManager = (
  namespace = 'default',
  concurrentLimit = DEFAULT_CONCURRENT_QUERY_LIMIT,
) => {
  // Base atom with concurrency limiter
  const queryManagerAtom = atom({
    namespace,
    concurrencyLimiter: concurrencyLimiter(concurrentLimit),
    status: {},
  });

  // Atom to modify query status
  const queryStatusAtom = atom(
    (get) => get(queryManagerAtom),
    (get, set, { entityId, action }) => {
      const currentState = get(queryManagerAtom);
      const entityIdStr = String(entityId);

      switch (action) {
        case 'inc':
          set(queryManagerAtom, {
            ...currentState,
            status: {
              ...currentState.status,
              [entityIdStr]: (currentState.status[entityIdStr] || 0) + 1,
            },
          });
          break;
        case 'dec':
          set(queryManagerAtom, {
            ...currentState,
            status: {
              ...currentState.status,
              [entityIdStr]: Math.max((currentState.status[entityIdStr] || 0) - 1, 0),
            },
          });
          break;
        case 'reset':
          set(queryManagerAtom, {
            ...currentState,
            status: {
              ...currentState.status,
              [entityIdStr]: 0,
            },
          });
          break;
        case 'clearQueue':
          currentState.concurrencyLimiter.clearQueue();
          set(queryManagerAtom, {
            ...currentState,
            status: {},
          });
          break;
      }
    },
  );

  /**
   * Hook to check if an entity is being queried
   */
  const useQueryStatus = (entityId) => {
    const queryManager = useAtomValue(queryManagerAtom);
    const status = queryManager.status;
    const queued = (status[String(entityId)] || 0) > 0;
    return { queued };
  };

  /**
   * Hook to get query manager for a specific entity
   */
  const useEntityQueryManager = (entityId) => {
    const [queryManager] = useAtom(queryManagerAtom);
    const [, setQueryStatus] = useAtom(queryStatusAtom);

    let cancelFn: any = undefined;

    const abort = () => {
      try {
        if (cancelFn) {
          cancelFn();
          setQueryStatus({ entityId, action: 'dec' });
        }
      } catch (err) {
        // ignore
      }
    };

    const exec = async (fn) => {
      abort();

      // Add query to queue
      setQueryStatus({ entityId, action: 'inc' });

      const { response, cancel } = queryManager.concurrencyLimiter(() => {
        // Switch to react query indicator
        setQueryStatus({ entityId, action: 'dec' });
        return fn();
      });

      cancelFn = cancel;
      const responseData = await response;
      cancelFn = undefined;
      return responseData;
    };

    return {
      exec,
      abort,
    };
  };

  /**
   * Hook to clear all queries
   */
  const useClearQueue = () => {
    const [, setQueryStatus] = useAtom(queryStatusAtom);
    return () => {
      setQueryStatus({ entityId: '', action: 'clearQueue' });
    };
  };

  return {
    queryManagerAtom,
    queryStatusAtom,
    useQueryStatus,
    useEntityQueryManager,
    useClearQueue,
  };
};

// Create the query manager for flows
const {
  useQueryStatus: useFlowQueryStatus,
  useEntityQueryManager: useFlowQueryManager,
  useClearQueue: useClearFlowQueue,
} = createQueryManager('flow');

// Create the query manager for frustration signals
const {
  useQueryStatus: useFrustrationSignalQueryStatus,
  useEntityQueryManager: useFrustrationSignalQueryManager,
  useClearQueue: useClearFrustrationSignalQueue,
} = createQueryManager('frustration-signal');

// Create the query manager for network panel (concurrency limit of 1 for sequential queries)
const {
  useQueryStatus: useNetworkQueryStatus,
  useEntityQueryManager: useNetworkQueryManager,
  useClearQueue: useClearNetworkQueue,
} = createQueryManager('network', 1);

// Create the query manager for events (similar to frustration signals)
const {
  useQueryStatus: useEventMetricsQueryStatus,
  useEntityQueryManager: useEventMetricsQueryManager,
  useClearQueue: useClearEventMetricsQueue,
} = createQueryManager('event-metrics');

// Legacy export for backward compatibility with old hook names
const {
  useQueryStatus: useEventsQueryStatus,
  useEntityQueryManager: useEventsQueryManager,
  useClearQueue: useClearEventsQueue,
} = createQueryManager('events');

// Export the query manager functions
export {
  useFlowQueryStatus,
  useFlowQueryManager,
  useClearFlowQueue,
  useFrustrationSignalQueryStatus,
  useFrustrationSignalQueryManager,
  useClearFrustrationSignalQueue,
  useNetworkQueryStatus,
  useNetworkQueryManager,
  useClearNetworkQueue,
  useEventMetricsQueryStatus,
  useEventMetricsQueryManager,
  useClearEventMetricsQueue,
  useEventsQueryStatus,
  useEventsQueryManager,
  useClearEventsQueue,
};
